import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { fieldValueToString } from "../../../utils/react_hook_form_input_utils";

/**
 * FormMaskedInputBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
  unmask: boolean;
  placeholderChar?: string;
}

const FormMaskedInputBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      placeholderChar={props.placeholderChar ?? ' '}
      inputRef={refs}
      onAccept={(value: any, input: { unmaskedValue: string }, e) => {
        if (input.unmaskedValue.length > 0) {
          onChange({ target: { name: props.name, value } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={props.unmask}
    />
  );
});

/**
 * FormMaskedInput
 */

type Props<T extends {}> = {
  name: keyof T,
  label: string,
  mask: any,
  maskPlaceholder: string,
  placeholderChar?: string;
  unmask?: boolean,
  required?: boolean,
  readOnly?: boolean,
  hasChangeLock?: boolean,
  reactHookForm: UseFormReturn<T>,
  onChange?: (newValue: string) => void,
  onBlur?: (newValue: string) => void,
};

export const FormMaskedInput = <T extends {},>({
  name,
  label,
  mask,
  maskPlaceholder,
  unmask = true,
  required,
  readOnly,
  reactHookForm,
  onChange,
  onBlur,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText: string | null = null;

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <FormControl variant="outlined" size="small" required={required} fullWidth={true}>
            <InputLabel
              htmlFor={`input-${name as string}`}
              error={helperText !== null}
              required={required}
              shrink={true}
              size="small">
              {label}
            </InputLabel>

            <OutlinedInput
              size="small"
              id={`input-${name as string}`}
              fullWidth={true}
              label={label}
              notched={true}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e.target.value);
              }}
              onBlur={(e) => {
                field.onBlur();
                onBlur?.(e.target.value);
              }}
              readOnly={readOnly}
              required={required}
              error={helperText !== null}
              aria-describedby={`${name as string}-component-error-text`}
              defaultValue={fieldValueToString(field.value) ?? ""}
              value={fieldValueToString(field.value) ?? ""}
              disabled={formState.disabled}
              inputRef={field.ref}
              inputComponent={FormMaskedInputBase as any}
              inputProps={{
                mask: mask,
                placeholder: maskPlaceholder,
                unmask: unmask,
              }} />

            <FormHelperText id={`${name as string}-component-error-text`} error={true}>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }} />
  );
}